/**
 * DV-310 Foundational Education links: pure helpers for the admin page (validation that mirrors
 * the backend, display order and reordering).
 */

export const TITLE_MAX = 255;
export const DESCRIPTION_MAX = 1000;
export const URL_MAX = 500;
export const SORT_STEP = 10;

const URL_PATTERN = /^https?:\/\/\S+$/;

const trimmed = (value) => (value || '').trim();

/** Display order: sortOrder ascending, then id. */
export const sortLinks = (links) => [...(links || [])].sort(
  (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || (a.id ?? 0) - (b.id ?? 0),
);

/** sortOrder for a new link: after every existing one. */
export const nextSortOrder = (links) => (links || []).reduce(
  (max, l) => Math.max(max, Number(l.sortOrder) || 0),
  0,
) + SORT_STEP;

/** The LinkRequest body for a link: its own fields (no id or audit fields), with overrides. */
export const linkToRequest = (link, overrides = {}) => ({
  titleEn: link.titleEn,
  titleEs: link.titleEs ?? null,
  descriptionEn: link.descriptionEn ?? null,
  descriptionEs: link.descriptionEs ?? null,
  urlEn: link.urlEn,
  urlEs: link.urlEs ?? null,
  sortOrder: link.sortOrder,
  active: !!link.active,
  ...overrides,
});

/** Form values from the editor, trimmed; blank optional fields become null. */
export const formToRequest = (form, sortOrder) => ({
  titleEn: trimmed(form.titleEn),
  titleEs: trimmed(form.titleEs) || null,
  descriptionEn: trimmed(form.descriptionEn) || null,
  descriptionEs: trimmed(form.descriptionEs) || null,
  urlEn: trimmed(form.urlEn),
  urlEs: trimmed(form.urlEs) || null,
  sortOrder,
  active: !!form.active,
});

const URL_ERROR = 'Must start with http:// or https://';

/**
 * Client-side mirror of the backend rules. Returns { field: message } for each problem; empty
 * when the form can be sent.
 */
export const validateLink = (form) => {
  const errors = {};
  const check = (field, max, { required, requiredMessage, url } = {}) => {
    const value = trimmed(form[field]);
    if (!value) {
      if (required) errors[field] = requiredMessage;
      return;
    }
    if (url && !URL_PATTERN.test(value)) errors[field] = URL_ERROR;
    else if (value.length > max) errors[field] = `At most ${max} characters`;
  };
  check('titleEn', TITLE_MAX, { required: true, requiredMessage: 'English title is required' });
  check('titleEs', TITLE_MAX);
  check('descriptionEn', DESCRIPTION_MAX);
  check('descriptionEs', DESCRIPTION_MAX);
  check('urlEn', URL_MAX, { required: true, requiredMessage: 'English link is required', url: true });
  check('urlEs', URL_MAX, { url: true });
  return errors;
};

/** "https://www.example.com/path/..." -> "www.example.com/path/..." cut to `max` characters. */
export const shortUrl = (url, max = 48) => {
  const bare = (url || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
  return bare.length > max ? `${bare.slice(0, max - 1)}…` : bare;
};

/**
 * Moving the link at `index` (in display order) one place up (-1) or down (+1). Returns the
 * links whose sortOrder has to change, as [{ link, sortOrder }]: normally the two neighbours
 * swap sortOrders; when that would not give the wanted order (they share a sortOrder, or ties
 * elsewhere would reshuffle), every link is renumbered 10, 20, 30... and only the changed ones
 * are returned. Empty when the move is not possible.
 */
export const planMove = (links, index, direction) => {
  const ordered = sortLinks(links);
  const target = index + direction;
  if (index < 0 || index >= ordered.length || target < 0 || target >= ordered.length) return [];

  const wanted = [...ordered];
  [wanted[index], wanted[target]] = [wanted[target], wanted[index]];

  const a = ordered[index];
  const b = ordered[target];
  if (a.sortOrder !== b.sortOrder) {
    const swapped = ordered.map((l) => {
      if (l === a) return { ...l, sortOrder: b.sortOrder };
      if (l === b) return { ...l, sortOrder: a.sortOrder };
      return l;
    });
    const resultIds = sortLinks(swapped).map((l) => l.id);
    if (resultIds.every((id, i) => id === wanted[i].id)) {
      return [{ link: a, sortOrder: b.sortOrder }, { link: b, sortOrder: a.sortOrder }];
    }
  }

  return wanted
    .map((link, i) => ({ link, sortOrder: (i + 1) * SORT_STEP }))
    .filter(({ link, sortOrder }) => link.sortOrder !== sortOrder);
};
